import { useEffect, useState, useMemo, useCallback } from "react";
import SideBar from "../components/SideBar";
import TopBar from "../components/TopBar";
import resourceicon from "../assets/image/resourceicon.svg";

const TABS_CONFIG = [
  { id: "all-article", label: "All Articles", category: "" },
  {
    id: "family-article",
    label: "Family & Devotion Articles",
    category: "family-devotion",
  },
  { id: "singles-article", label: "Singles Articles", category: "singles" },
  {
    id: "children-article",
    label: "Children Articles",
    category: "children",
  },
  { id: "others-article", label: "Others", category: "others" },
];

export default function Resources() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all-article");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const itemsPerPage = 6;

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const activeCategory = useMemo(() => {
    return TABS_CONFIG.find((t) => t.id === activeTab)?.category || "";
  }, [activeTab]);
  const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;
  
  const fetchArticles = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");

      const url = activeCategory
        ? `${TEST_BASE_URL}/articles?category=${activeCategory}&page=${currentPage}&per_page=${itemsPerPage}`
        : `${TEST_BASE_URL}/articles?page=${currentPage}&per_page=${itemsPerPage}`;
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      let articlesData = [];
      let pagination = {};

      if (data.data?.articles) {
        articlesData = data.data.articles;
        pagination = data.data.pagination || {};
      } else if (data.articles) {
        articlesData = data.articles;
        pagination = data.pagination || {};
      } else if (data.data && Array.isArray(data.data)) {
        articlesData = data.data;
        pagination = data.pagination || {};
      } else if (Array.isArray(data)) {
        articlesData = data;
      }

      const totalCount =
        pagination.total || pagination.total_items || articlesData.length || 0;
      const totalPages =
        pagination.last_page ||
        pagination.total_pages ||
        Math.ceil(totalCount / itemsPerPage);

      setArticles(articlesData);
      setTotalArticles(totalCount);
      setTotalPages(totalPages);
    } catch (err) {
      console.error("Fetch Articles Error:", err);
      setError(`Unable to load articles: ${err.message}`);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, currentPage, TEST_BASE_URL]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, activeCategory]);

  const activeTabLabel =
    TABS_CONFIG.find((t) => t.id === activeTab)?.label || "Resources";

  return (
    <section className="dashboard">
      <SideBar isOpen={sidebarOpen} />
      <div className="body-content">
        <TopBar onToggleSidebar={toggleSidebar} />
        <div className="bottom-content">
          <div className="prayer-title">
            <span>Resources</span>
          </div>

          <div className="resources">
            <div className="resources-wrap">
              <div className="resource-title-wrap">
                <img src={resourceicon} alt="Resource Icon" />
                <span>{activeTabLabel}</span>
              </div>
              <div className="resource-divider-wrap">
                <div className="divider" />
                <span>
                  Discover how to walk by faith, not by sight, in your everyday
                  <br />
                  challenges. These articles encourage trust in God's
                  <br />
                  promises no matter the season.
                </span>
              </div>
            </div>
            <div className="article-section">
              <div className="article-row">
                {TABS_CONFIG.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`article-tab ${
                      activeTab === t.id ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(t.id)}
                    aria-pressed={activeTab === t.id}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="container article-container">
                {loading ? (
                  <div className="loading-state">
                    <p>Loading articles...</p>
                  </div>
                ) : error ? (
                  <div className="error-state">
                    <p className="error-text">{error}</p>
                    <button
                      onClick={() => setActiveTab(activeTab)}
                      className="btn btn-retry"
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <div
                    className={`row article-content ${
                      loading ? "loading" : ""
                    }`}
                  >
                    {articles.length > 0 ? (
                      articles.map((a) => (
                        <div className="col-md-4 colmd" key={a.id}>
                          <div className="resource-card" key={a.id}>
                            {a.thumbnail ? (
                              <img
                                src={
                                  a.thumbnail.startsWith("http")
                                    ? a.thumbnail
                                    : `https://rccgbournemouth.org${
                                        a.thumbnail.startsWith("/") ? "" : "/"
                                      }${a.thumbnail}`
                                }
                                alt={a.title || "Article Image"}
                                className="card-img-top"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className="fallback-text"
                              style={{ display: "none" }}
                            >
                              <p>{a.title || "Image not available"}</p>
                            </div>

                            <div className="cardbody">
                              <h5 className="card-title">
                                {a.title || "Untitled"}
                              </h5>
                              <p className="card-text">
                                {a.body?.slice(0, 120) ||
                                  a.sub_title ||
                                  "No description available..."}
                              </p>
                              <a
                                href={`/resources-article/${a.id}`}
                                className="btn btn-read-more"
                              >
                                Read More
                              </a>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 empty-state">
                        <p>No articles found in this category.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {!loading && articles.length > 0 && (
              <div className="pagination-section">
                <span className="pagination-info">
                  Showing{" "}
                  <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> –{" "}
                  <strong>
                    {Math.min(currentPage * itemsPerPage, totalArticles)}
                  </strong>{" "}
                  of <strong>{totalArticles}</strong> articles
                </span>

                <div className="pagination-container">
                  <button
                    className="pre-button"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>

                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    const isVisible =
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1);

                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={`ellipsis-${page}`} className="ellipsis">
                          ...
                        </span>
                      );
                    }

                    if (!isVisible) return null;

                    return (
                      <button
                        key={page}
                        className={`page-button ${
                          currentPage === page ? "active" : ""
                        }`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    className="next-button"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
